import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { api, errText } from "../lib/api";
import { useStore } from "../context/Store";
import { t } from "../i18n";
import { Trash2, BellRing, Repeat, CreditCard, LayoutDashboard, ShoppingBag, Heart, MapPin, Bell, Sliders, User, Home, Users } from "lucide-react";
import { DateField, fmtDate } from "../components/DateField";
import { MOROCCO } from "../lib/countries";
import Seo from "../components/Seo";
import {
  OverviewPanel, WishlistPanel, ProfilePanel, PreferencesPanel, BillingAddressPanel, PaymentMethodsPanel, SidebarSignOut,
} from "./AccountPanels";

const AuthPanel = ({ locale, mode, setMode }) => {
  const { refreshAccount, boot } = useStore();
  const [form, setForm] = useState({ email: "", password: "", name: "", order_number: "" });
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const _ = boot;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setInfo("");
    try {
      if (mode === "signin") {
        const { data } = await api.post("/auth/login", { email: form.email, password: form.password });
        if (data.user.role !== "buyer") throw new Error("This is a staff account. Use the dashboard sign in.");
        localStorage.setItem("fl_token", data.token);
      } else if (mode === "signup") {
        const { data } = await api.post("/auth/register", {
          email: form.email, password: form.password, name: form.name, locale,
          order_number: form.order_number || "",
        });
        localStorage.setItem("fl_token", data.token);
      } else {
        await api.post("/auth/forgot-password", { email: form.email, origin_url: window.location.origin, locale });
        setInfo(t(locale, "reminderSaved"));
        return;
      }
      await refreshAccount();
    } catch (err) {
      setError(errText(err));
    }
  };

  return (
    <form onSubmit={submit} className="card p-6 max-w-md space-y-3" data-testid="account-auth-panel">
      <h1 className="font-display text-2xl text-ink">
        {mode === "signin" ? t(locale, "signIn") : mode === "signup" ? t(locale, "signUp") : t(locale, "forgotPassword")}
      </h1>
      <p className="text-sm text-muted">{t(locale, "accountBenefits")}</p>
      {mode === "signup" ? (
        <input data-testid="account-name" className="field" placeholder={t(locale, "senderName")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
      ) : null}
      <input data-testid="account-email" className="field" placeholder={t(locale, "buyerEmail")} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
      {mode !== "forgot" ? (
        <input data-testid="account-password" type="password" className="field" placeholder={t(locale, "password")} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
      ) : null}
      {mode === "signup" ? (
        <input data-testid="account-order-number" className="field" placeholder={t(locale, "orderNumber")} value={form.order_number} onChange={(e) => setForm({ ...form, order_number: e.target.value.toUpperCase() })} />
      ) : null}
      {error ? <p className="text-danger text-sm" data-testid="account-error">{error}</p> : null}
      {info ? <p className="text-success text-sm" data-testid="account-info">{info}</p> : null}
      <button data-testid="account-submit" className="btn-primary w-full">
        {mode === "signin" ? t(locale, "signIn") : mode === "signup" ? t(locale, "createAccount") : t(locale, "forgotPassword")}
      </button>
      <div className="flex gap-4 text-xs text-muted">
        <button type="button" data-testid="switch-signin" onClick={() => setMode("signin")}>{t(locale, "signIn")}</button>
        <button type="button" data-testid="switch-signup" onClick={() => setMode("signup")}>{t(locale, "signUp")}</button>
        <button type="button" data-testid="switch-forgot" onClick={() => setMode("forgot")}>{t(locale, "forgotPassword")}</button>
      </div>
    </form>
  );
};

export default function Account() {
  const { locale = "en" } = useParams();
  const { account, boot, signOut, refreshAccount } = useStore();
  const [mode, setMode] = useState("signin");
  const [tab, setTab] = useState("overview");
  const [orders, setOrders] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [wishlist, setWishlist] = useState([]);
  const [me, setMe] = useState(null);
  const [error, setError] = useState("");
  const [newRec, setNewRec] = useState({ name: "", relationship: "", phone: "", address: "", city_id: "", zone_id: "", landmark_note: "" });
  const [zones, setZones] = useState([]);
  const [newRem, setNewRem] = useState({ recipient_id: "", occasion_id: "", date: "", lead_days: 7, channels: ["email"], whatsapp: "" });
  const [subs, setSubs] = useState([]);

  const load = () => {
    api.get("/account/orders", { params: { locale } }).then((r) => setOrders(r.data)).catch(() => {});
    api.get("/account/recipients", { params: { locale } }).then((r) => setRecipients(r.data)).catch(() => {});
    api.get("/account/reminders", { params: { locale } }).then((r) => setReminders(r.data)).catch(() => {});
    api.get("/account/subscriptions", { params: { locale } }).then((r) => setSubs(r.data)).catch(() => {});
    api.get("/account/wishlist", { params: { locale } }).then((r) => setWishlist(r.data)).catch(() => setWishlist([]));
    api.get("/account/me").then((r) => setMe(r.data)).catch(() => {});
  };

  useEffect(() => {
    if (account) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account, locale]);

  useEffect(() => {
    if (!newRec.city_id) return;
    const city = (boot?.cities || []).find((c) => c.id === newRec.city_id);
    if (city) api.get(`/cities/${city.slug}`, { params: { locale } }).then((r) => setZones(r.data.zones)).catch(() => {});
  }, [newRec.city_id, boot, locale]);

  if (account === null) return <div className="max-w-content mx-auto px-6 py-24 text-muted">{t(locale, "loading")}…</div>;
  if (!account)
    return (
      <div className="max-w-content mx-auto px-6 py-16" data-testid="account-page-guest">
        <Seo title={`${t(locale, "signIn")} | Fleurinelle`} noindex />
        <AuthPanel locale={locale} mode={mode} setMode={setMode} />
      </div>
    );

  const addRecipient = async () => {
    setError("");
    try {
      await api.post("/account/recipients", { ...newRec, phone: newRec.phone ? `+212${newRec.phone.replace(/[\s-]/g, "").replace(/^0+/, "")}` : "" });
      setNewRec({ name: "", relationship: "", phone: "", address: "", city_id: "", zone_id: "", landmark_note: "" });
      load();
    } catch (e) {
      setError(errText(e));
    }
  };

  const addReminder = async () => {
    setError("");
    try {
      await api.post("/account/reminders", newRem);
      setNewRem({ recipient_id: "", occasion_id: "", date: "", lead_days: 7, channels: ["email"], whatsapp: "" });
      load();
    } catch (e) {
      setError(errText(e));
    }
  };

  const NAV = [
    ["overview", t(locale, "navOverview"), LayoutDashboard],
    ["orders", t(locale, "navOrders"), ShoppingBag],
    ["subscriptions", t(locale, "navRecurring"), Repeat],
    ["wishlist", t(locale, "navWishlist"), Heart],
    ["recipients", t(locale, "navRecipients"), Users],
    ["reminders", t(locale, "navReminders"), Bell],
    ["profile", t(locale, "navProfile"), User],
    ["preferences", t(locale, "navPreferences"), Sliders],
    ["billing", t(locale, "navBilling"), MapPin],
    ["payment-methods", t(locale, "navSavedCards"), CreditCard],
  ];

  return (
    <div className="max-w-content mx-auto px-4 md:px-6 py-10 md:py-16 grid md:grid-cols-[220px_1fr] gap-6 md:gap-10" data-testid="account-page">
      <Seo title={`${t(locale, "account")} | Fleurinelle`} noindex />
      <aside className="md:sticky md:top-6 md:self-start md:h-fit space-y-1" data-testid="account-sidebar">
        <div className="mb-4 px-1">
          <p className="smallcaps text-brass">{t(locale, "accHeading")}</p>
          <p className="text-sm text-ink font-medium truncate">{account.name || account.email}</p>
          <p className="text-[11px] text-muted truncate">{account.email}</p>
        </div>
        {NAV.map(([k, label, Icon]) => (
          <button key={k} data-testid={`account-tab-${k}`} onClick={() => setTab(k)}
                  className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 rounded-sm transition-colors
                              ${tab === k ? "bg-plaster-deep text-ink" : "text-muted hover:text-ink hover:bg-plaster"}`}>
            <Icon size={15} className={tab === k ? "text-brass" : ""} />
            <span className="truncate">{label}</span>
          </button>
        ))}
        <Link to={`/${locale}`} className="w-full text-left px-3 py-2 text-xs text-muted hover:text-ink flex items-center gap-2">
          <Home size={13} /> {t(locale, "accBackStore")}
        </Link>
        <SidebarSignOut onSignOut={signOut} locale={locale} />
      </aside>

      <div className="min-w-0 space-y-6">
        <h1 className="font-display text-3xl text-ink">
          {NAV.find(([k]) => k === tab)?.[1] || t(locale, "accFallbackTitle")}
        </h1>

        {error ? <p className="text-danger text-sm" data-testid="account-action-error">{error}</p> : null}

        {tab === "overview" ? (
          <OverviewPanel account={account} orders={orders} wishlist={wishlist} reminders={reminders} locale={locale} onGoto={setTab} />
        ) : null}

        {tab === "wishlist" ? <WishlistPanel locale={locale} /> : null}
        {tab === "profile" ? <ProfilePanel account={account} me={me} locale={locale} onRefresh={() => { refreshAccount(); api.get("/account/me").then((r) => setMe(r.data)).catch(() => {}); }} onSignedOut={() => refreshAccount()} /> : null}
        {tab === "preferences" ? <PreferencesPanel me={me} locale={locale} currencies={boot?.currencies} onSaved={() => api.get("/account/me").then((r) => setMe(r.data)).catch(() => {})} /> : null}
        {tab === "billing" ? <BillingAddressPanel me={me} locale={locale} onSaved={() => api.get("/account/me").then((r) => setMe(r.data)).catch(() => {})} /> : null}
        {tab === "payment-methods" ? <PaymentMethodsPanel locale={locale} /> : null}

      {tab === "orders" ? (
        <div className="space-y-3" data-testid="account-orders">
          {orders.length === 0 ? <p className="text-sm text-muted">{t(locale, "noResults")}</p> : null}
          {orders.map((o) => (
            <div key={o.order_number} className="card p-4 flex items-center gap-4" data-testid={`account-order-${o.order_number}`}>
              {o.image ? <img src={o.image} alt="" className="w-14 aspect-[4/5] object-cover" /> : null}
              <div className="text-sm flex-1">
                <p className="text-ink">{o.order_number} · {o.recipient_name}</p>
                <p className="text-xs text-muted">{o.city} · {fmtDate(o.delivery_date)} · {o.status_label}</p>
              </div>
              <button className="btn-ghost text-[11px] py-1.5 px-3" data-testid={`save-recipient-${o.order_number}`} onClick={async () => {
                await api.post(`/account/recipients/from-order/${o.order_number}`);
                load();
                setTab("recipients");
              }}>
                {t(locale, "saveRecipient")}
              </button>
              <Link className="btn-ghost text-[11px] py-1.5 px-3" to={`/${locale}/track?order=${o.order_number}&email=${account.email}`}>
                {t(locale, "viewOrder")}
              </Link>
            </div>
          ))}
        </div>
      ) : null}

      {tab === "subscriptions" ? (
        <div className="space-y-3" data-testid="account-subscriptions">
          {subs.length === 0 ? <p className="text-sm text-muted" data-testid="no-subscriptions">{t(locale, "noSubscriptions")}</p> : null}
          {subs.map((s) => (
            <div key={s.id} className="card p-4 flex flex-col sm:flex-row gap-4" data-testid={`subscription-${s.subscription_number}`}>
              {s.image ? <img src={s.image} alt="" className="w-16 aspect-[4/5] object-cover" /> : null}
              <div className="text-sm flex-1 space-y-1">
                <p className="text-ink flex items-center gap-2">
                  <Repeat size={14} className="text-brass" />
                  {s.subscription_number} · {s.title}
                </p>
                <p className="text-xs text-muted">
                  {s.plan} · {s.recipient_name} · {s.city} · {s.status}
                  {s.discount_percent ? ` · −${s.discount_percent}%` : ""}
                </p>
                <p className="text-xs text-muted tabular" data-testid={`subscription-price-${s.subscription_number}`}>
                  {s.symbol} {s.amount} · {s.deliveries_done} {t(locale, "deliveriesSent")}
                </p>
                {s.next_delivery_date ? (
                  <p className="text-xs text-ink tabular" data-testid={`subscription-next-${s.subscription_number}`}>
                    {t(locale, "nextDelivery")}: {fmtDate(s.next_delivery_date)}
                  </p>
                ) : null}
                {s.cancel_at_period_end ? <p className="text-xs text-warning">{t(locale, "planCancelled")}</p> : null}
                {s.last_error ? <p className="text-xs text-danger">{s.last_error}</p> : null}
              </div>
              <div className="flex flex-wrap gap-2 items-start">
                {s.next_delivery_date ? (
                  <button className="btn-ghost text-[11px] py-1.5 px-3" data-testid={`skip-subscription-${s.subscription_number}`}
                          onClick={async () => { try { await api.post(`/account/subscriptions/${s.id}/skip`); load(); } catch (e) { setError(errText(e)); } }}>
                    {t(locale, "skipNext")}
                  </button>
                ) : null}
                <button className="btn-ghost text-[11px] py-1.5 px-3 inline-flex items-center gap-1" data-testid={`portal-subscription-${s.subscription_number}`}
                        onClick={async () => {
                          setError("");
                          try {
                            const { data } = await api.post(`/account/subscriptions/${s.id}/portal`, { return_url: window.location.href });
                            window.location.href = data.url;
                          } catch (e) { setError(errText(e)); }
                        }}>
                  <CreditCard size={12} /> {t(locale, "manageBilling")}
                </button>
                {["active", "payment_failed"].includes(s.status) ? (
                  <button className="btn-ghost text-[11px] py-1.5 px-3 text-danger" data-testid={`cancel-subscription-${s.subscription_number}`}
                          onClick={async () => { try { await api.post(`/account/subscriptions/${s.id}/cancel`, { at_period_end: true }); load(); } catch (e) { setError(errText(e)); } }}>
                    {t(locale, "cancelPlan")}
                  </button>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      ) : null}

      {tab === "recipients" ? (
        <div className="grid lg:grid-cols-2 gap-8" data-testid="account-recipients">
          <div className="space-y-3">
            {recipients.length === 0 ? <p className="text-sm text-muted">{t(locale, "noResults")}</p> : null}
            {recipients.map((r) => (
              <div key={r.id} className="card p-4 text-sm flex items-start gap-3" data-testid={`recipient-${r.id}`}>
                <div className="flex-1">
                  <p className="text-ink">{r.name} {r.relationship ? <span className="text-muted">· {r.relationship}</span> : null}</p>
                  <p className="text-xs text-muted">{r.city_name} · {r.zone_name} · {r.phone}</p>
                  <p className="text-xs text-muted">{r.address}</p>
                </div>
                <button data-testid={`delete-recipient-${r.id}`} onClick={async () => { await api.delete(`/account/recipients/${r.id}`); load(); }} className="text-muted hover:text-danger">
                  <Trash2 size={15} />
                </button>
              </div>
            ))}
          </div>
          <div className="card p-5 space-y-3">
            <h2 className="font-display text-xl text-ink">{t(locale, "saveRecipient")}</h2>
            <input data-testid="new-recipient-name" className="field" placeholder={t(locale, "fullName")} value={newRec.name} onChange={(e) => setNewRec({ ...newRec, name: e.target.value })} />
            <input data-testid="new-recipient-relationship" className="field" placeholder={t(locale, "relationship")} value={newRec.relationship} onChange={(e) => setNewRec({ ...newRec, relationship: e.target.value })} />
            <div className="flex gap-2">
              <span className="field !w-[86px] flex items-center justify-center gap-1 text-sm whitespace-nowrap shrink-0">{MOROCCO.flag} {MOROCCO.dial}</span>
              <input data-testid="new-recipient-phone" className="field flex-1" type="tel" inputMode="tel" placeholder="6 12 34 56 78" value={newRec.phone} onChange={(e) => setNewRec({ ...newRec, phone: e.target.value })} />
            </div>
            <textarea data-testid="new-recipient-address" className="field h-20" placeholder={t(locale, "address")} value={newRec.address} onChange={(e) => setNewRec({ ...newRec, address: e.target.value })} />
            <select data-testid="new-recipient-city" className="field" value={newRec.city_id} onChange={(e) => setNewRec({ ...newRec, city_id: e.target.value, zone_id: "" })}>
              <option value="">{t(locale, "chooseCity")}</option>
              {(boot?.cities || []).map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            <select data-testid="new-recipient-zone" className="field" value={newRec.zone_id} onChange={(e) => setNewRec({ ...newRec, zone_id: e.target.value })}>
              <option value="">{t(locale, "zone")}</option>
              {zones.map((z) => (
                <option key={z.id} value={z.id}>{z.name}</option>
              ))}
            </select>
            <input data-testid="new-recipient-landmark" className="field" placeholder={t(locale, "landmark")} value={newRec.landmark_note} onChange={(e) => setNewRec({ ...newRec, landmark_note: e.target.value })} />
            <button data-testid="new-recipient-submit" className="btn-primary w-full" onClick={addRecipient} disabled={!newRec.name || !newRec.city_id || !newRec.zone_id}>
              {t(locale, "saveRecipient")}
            </button>
          </div>
        </div>
      ) : null}

      {tab === "reminders" ? (
        <div className="grid lg:grid-cols-2 gap-8" data-testid="account-reminders">
          <div className="space-y-3">
            {reminders.length === 0 ? <p className="text-sm text-muted">{t(locale, "noResults")}</p> : null}
            {reminders.map((r) => (
              <div key={r.id} className="card p-4 text-sm flex items-start gap-3" data-testid={`reminder-${r.id}`}>
                <BellRing size={16} className="text-brass mt-0.5" />
                <div className="flex-1">
                  <p className="text-ink">{r.occasion_name} · {r.recipient_name}</p>
                  <p className="text-xs text-muted tabular">{fmtDate(r.date)} · {r.lead_days} {t(locale, "leadDays")} · {r.channels.join(", ")}</p>
                </div>
                <button data-testid={`delete-reminder-${r.id}`} onClick={async () => { await api.delete(`/account/reminders/${r.id}`); load(); }} className="text-muted hover:text-danger">
                  <Trash2 size={15} />
                </button>
              </div>
            ))}
          </div>
          <div className="card p-5 space-y-3">
            <h2 className="font-display text-xl text-ink">{t(locale, "addReminder")}</h2>
            <select data-testid="new-reminder-recipient" className="field" value={newRem.recipient_id} onChange={(e) => setNewRem({ ...newRem, recipient_id: e.target.value })}>
              <option value="">{t(locale, "useSaved")}</option>
              {recipients.map((r) => (
                <option key={r.id} value={r.id}>{r.name}</option>
              ))}
            </select>
            <select data-testid="new-reminder-occasion" className="field" value={newRem.occasion_id} onChange={(e) => setNewRem({ ...newRem, occasion_id: e.target.value })}>
              <option value="">{t(locale, "chooseOccasion")}</option>
              {(boot?.occasions || []).map((o) => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
            </select>
            <DateField testid="new-reminder-date" value={newRem.date} onChange={(v) => setNewRem({ ...newRem, date: v })} />
            <label className="block text-sm text-muted">
              {t(locale, "remindMe")} {newRem.lead_days} {t(locale, "leadDays")}
              <input data-testid="new-reminder-lead" type="range" min="1" max="30" className="w-full" value={newRem.lead_days} onChange={(e) => setNewRem({ ...newRem, lead_days: Number(e.target.value) })} />
            </label>
            <div className="flex gap-4 text-sm">
              <label className="flex items-center gap-2">
                <input type="checkbox" checked readOnly /> Email
              </label>
              <label className="flex items-center gap-2">
                <input data-testid="new-reminder-whatsapp-toggle" type="checkbox" checked={newRem.channels.includes("whatsapp")} onChange={(e) => setNewRem({ ...newRem, channels: e.target.checked ? ["email", "whatsapp"] : ["email"] })} />
                WhatsApp
              </label>
            </div>
            {newRem.channels.includes("whatsapp") ? (
              <input data-testid="new-reminder-whatsapp" className="field" placeholder={t(locale, "whatsappNumber")} value={newRem.whatsapp} onChange={(e) => setNewRem({ ...newRem, whatsapp: e.target.value })} />
            ) : null}
            <button data-testid="new-reminder-submit" className="btn-primary w-full" onClick={addReminder} disabled={!newRem.occasion_id || !newRem.date || !newRem.recipient_id}>
              {t(locale, "addReminder")}
            </button>
          </div>
        </div>
      ) : null}
      </div>
    </div>
  );
}

export const ResetPassword = () => {
  const { locale = "en" } = useParams();
  const [params] = useSearchParams();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [done, setDone] = useState(false);
  const [portal, setPortal] = useState("");
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { setError(t(locale, "passwordsMismatch")); return; }
    try {
      const { data } = await api.post("/auth/reset-password", { token: params.get("token"), password });
      setPortal(data?.portal || "");
      setDone(true);
    } catch (err) {
      setError(errText(err));
    }
  };
  return (
    <form onSubmit={submit} className="max-w-md mx-auto px-6 py-24 space-y-4" data-testid="reset-password-page">
      <Seo title={`${t(locale, "resetPassword")} | Fleurinelle`} noindex />
      <h1 className="font-display text-3xl text-ink">{done ? t(locale, "passwordUpdated") : t(locale, "resetPassword")}</h1>
      {done ? (
        <div className="bg-success/10 border border-success/30 p-4 rounded-sm text-sm text-ink space-y-2" data-testid="reset-done">
          <p>✓ {t(locale, "passwordUpdated")}.</p>
          {portal ? <a className="btn-primary inline-block" href={portal} data-testid="reset-portal-link">{t(locale, "signIn")}</a>
                  : <Link className="btn-primary inline-block" to={`/${locale}/account`}>{t(locale, "signIn")}</Link>}
        </div>
      ) : (
        <>
          <input data-testid="reset-password-input" type="password" className="field" minLength={8} placeholder={t(locale, "newPassword")} value={password} onChange={(e) => setPassword(e.target.value)} />
          <input data-testid="reset-password-confirm" type="password" className="field" minLength={8} placeholder={t(locale, "confirmPassword")} value={confirm} onChange={(e) => setConfirm(e.target.value)} />
          {error ? <p className="text-danger text-sm" data-testid="reset-error">{error}</p> : null}
          <button className="btn-primary w-full" data-testid="reset-submit">{t(locale, "resetPassword")}</button>
        </>
      )}
    </form>
  );
};
